import React, { useState, useMemo, useEffect } from "react";
import { Box, Checkbox, TextField, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";

interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

interface CategoryTreeSelectProps {
  categories: Category[];
  toggleCategory: (selectedCategoryKey: string) => void;
  selectedCategoryKeys: Set<string>;
  isSingleSelect: boolean;
  isDisabledCheckboxes?: boolean;
}

/** Recursively collect the keys of parent categories for a given key */
function findParentKeys(categories: Category[], targetKey: string, path: string[] = []): string[] {
  for (const category of categories) {
    const currentPath = [...path, category.key];
    if (category.key === targetKey) {
      return currentPath.slice(0, -1); // Exclude the target itself
    }

    if (category.subcategories.length > 0) {
      const result = findParentKeys(category.subcategories, targetKey, currentPath);
      if (result.length > 0) {
        return result;
      }
    }
  }
  return [];
}

/** Filter categories recursively */
function filterCategories(categories: Category[], searchText: string): Category[] {
  const lowerSearch = searchText.toLowerCase();

  return categories
    .map((cat) => {
      const filteredSubs = filterCategories(cat.subcategories, searchText);
      const nameMatches = cat.name.toLowerCase().includes(lowerSearch);

      if (nameMatches || filteredSubs.length > 0) {
        return { ...cat, subcategories: filteredSubs };
      }
      return null;
    })
    .filter(Boolean) as Category[];
}

const CategoryTreeSelect: React.FC<CategoryTreeSelectProps> = ({
  categories,
  toggleCategory,
  selectedCategoryKeys,
  isSingleSelect,
  isDisabledCheckboxes,
}) => {
  const [searchText, setSearchText] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  /** Compute expanded keys on load or when selected categories change */
  useEffect(() => {
    const expanded = new Set<string>();
    selectedCategoryKeys.forEach((key) => {
      const parents = findParentKeys(categories, key);
      parents.forEach((parentKey) => expanded.add(parentKey));
    });
    setExpandedKeys(Array.from(expanded));
  }, [selectedCategoryKeys, categories]);

  /** Handle category toggle */
  const handleCategoryToggle = (categoryKey: string) => {
    if (isSingleSelect) {
      toggleCategory(categoryKey);
    } else {
      toggleCategory(categoryKey);
    }
  };

  /** Render category tree */
  const renderCategoryTree = (categories: Category[]): React.ReactNode => {
    return categories.map((cat) => {
      const isChecked = selectedCategoryKeys.has(cat.key);

      return (
        <TreeItem
          key={cat.key}
          itemId={cat.key}
          label={
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={isChecked}
                onChange={() => handleCategoryToggle(cat.key)}
                size="small"
                sx={{ mr: 1 }}
                disabled={isDisabledCheckboxes}
              />
              <Typography variant="body2">{cat.name}</Typography>
            </Box>
          }
        >
          {cat.subcategories.length > 0 && renderCategoryTree(cat.subcategories)}
        </TreeItem>
      );
    });
  };

  // Filter the category tree for display
  const filteredCategories = useMemo(() => filterCategories(categories, searchText), [categories, searchText]);

  return (
    <Box>
      <Typography variant="subtitle2">Categories</Typography>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        placeholder="Search categories..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ my: 2 }}
      />
      <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
        <SimpleTreeView
          multiSelect={!isSingleSelect}
          expandedItems={expandedKeys} // Dynamically expanded items
          onExpandedItemsChange={(e, newExpanded) => setExpandedKeys(newExpanded)} // Keep track of user-expanded items
        >
          {renderCategoryTree(filteredCategories)}
        </SimpleTreeView>
      </Box>
    </Box>
  );
};

export default CategoryTreeSelect;
